// src

// What? A React component for managing a workspace's settings.
// Why? To allow the user to edit the workspace's description and assistant instructions.
// How?
// - Fetches workspace data using workspaceReadAPI.
// - Updates workspace data using workspaceUpdateAPI.
// - Handles beforeunload events to prevent data loss.
// - Displays the workspace's description and assistant instructions.
// - Allows the user to edit and save the description and assistant instructions.

import React, { useState, useEffect, useCallback } from "react";
import {
  useWorkspaceUpdate,
  useAvailableModules,
  useWorkspaceSkillOfferings,
  useWorkspaceSkillOfferingToggle,
  useWorkspaceFeatureFlags,
  useWorkspaceModuleConfig,
  useUpdateWorkspaceMode,
} from "../services/api";
import {
  Box,
  Button,
  Textarea,
  Input,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Typography,
  Stack,
  Card,
  Chip,
  Alert,
  CircularProgress,
  Snackbar,
  Switch,
  Tooltip,
  IconButton,
  FormHelperText,
  AccordionGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemDecorator,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/joy";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { useWorkspaceContext } from "../providers/WorkspaceProvider";
import { useFeatureFlagsContext } from "../providers/FeatureFlagProvider";
import {
  useTutorialTarget,
  useTutorialHint,
  useTutorial,
} from "../components/Tutorial";
import { isDevFeaturesEnabled } from "../utils/devFeatures";
import { moduleRegistry } from "../modules/moduleRegistry";
import PublicSignupToggle from "../components/Dashboard/PublicSignupToggle";
import WorkspaceIntegrationSelections from "../components/WorkspaceIntegrationSelections";
import { useOrgEmailStatus } from "../hooks/useOrgEmailStatus";
import { buildWorkspaceSettingsUpdatePayload } from "./workspaceSettingsUtils";
import { useIntegrationCatalog } from "../services/integrationCatalog";
import { filterWorkspaceSkillOfferingsByIntegrations } from "./workspaceSkillOfferingVisibility";
import {
  WorkspaceBaselineSkillRead,
  WorkspaceSkillOfferingRead,
  WorkspaceSkillOfferingsResponse,
  WorkspaceModuleConfigResponse,
} from "../common/types";
import RecentlyDecommissionedPanel from "./RecentlyDecommissionedPanel";
import WorkspaceCustomSkills from "../components/WorkspaceCustomSkills";

interface WorkspaceSkillOfferingCategoryGroup {
  key: string;
  label: string;
  offerings: WorkspaceSkillOfferingRead[];
}

const normalizeCategoryLabel = (value?: string | null) =>
  (value || "Uncategorized")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getSkillOfferingCategoryKey = (category?: string | null) =>
  (category || "uncategorized")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\s/g, "_") || "uncategorized";

const groupWorkspaceSkillOfferingsByCategory = (
  offerings: WorkspaceSkillOfferingRead[],
): WorkspaceSkillOfferingCategoryGroup[] => {
  const groups = new Map<string, WorkspaceSkillOfferingCategoryGroup>();
  offerings.forEach((offering) => {
    const key = getSkillOfferingCategoryKey(offering.category);
    const existing = groups.get(key);
    if (existing) {
      existing.offerings.push(offering);
      return;
    }
    groups.set(key, {
      key,
      label: normalizeCategoryLabel(offering.category),
      offerings: [offering],
    });
  });

  return Array.from(groups.values()).sort((left, right) =>
    left.label.localeCompare(right.label),
  );
};

const WorkspaceSettings = () => {
  // Email admin UI is behind a feature flag until user-tested
  const workspaceUpdateAPI = useWorkspaceUpdate();
  const getAvailableModules = useAvailableModules();
  const getSkillOfferings = useWorkspaceSkillOfferings();
  const toggleSkillOffering = useWorkspaceSkillOfferingToggle();
  const getFeatureFlags = useWorkspaceFeatureFlags();
  const getWorkspaceModuleConfig = useWorkspaceModuleConfig();
  const {
    workspace,
    enabledModules: contextEnabledModules,
    isAdmin,
    refreshWorkspace,
  } = useWorkspaceContext(); // Get workspace and enabled modules from context
  const {
    flags: environmentFeatureFlags,
    loading: featureFlagsLoading,
    refreshFlags,
  } = useFeatureFlagsContext();
  const orgEmailStatus = useOrgEmailStatus(
    workspace?.organization_id ?? undefined,
    isAdmin,
  );
  const [description, setDescription] = useState<string>(
    workspace?.description || "",
  );
  const [assistantInstructions, setAssistantInstructions] = useState<string>(
    workspace?.assistant_instructions || "",
  );
  const [enabledModules, setEnabledModules] = useState<string[]>(
    contextEnabledModules || [],
  );
  const [initialDescription, setInitialDescription] = useState<string>(
    workspace?.description || "",
  );
  const [initialAssistantInstructions, setInitialAssistantInstructions] =
    useState<string>(workspace?.assistant_instructions || "");
  const [initialEnabledModules, setInitialEnabledModules] = useState<string[]>(
    contextEnabledModules || [],
  );
  const [isChanged, setIsChanged] = useState<boolean>(false);
  const [workspaceName, setWorkspaceName] = useState<string>(
    workspace?.name || "",
  );
  const persistedPublicSignup = workspace?.features?.public_signup === true;
  const [publicSignup, setPublicSignup] = useState<boolean>(
    persistedPublicSignup,
  );
  const [publicSignupSaving, setPublicSignupSaving] = useState<boolean>(false);
  const [publicSignupError, setPublicSignupError] = useState<string | null>(
    null,
  );
  const [workspaceSaveError, setWorkspaceSaveError] = useState<string | null>(
    null,
  );
  const [moduleFeatureSchema, setModuleFeatureSchema] = useState<
    WorkspaceModuleConfigResponse["feature_schema"]
  >({});
  const [moduleFeatureValues, setModuleFeatureValues] = useState<
    Record<string, boolean | string | number>
  >({});
  const [moduleFeatureGates, setModuleFeatureGates] = useState<
    Record<string, boolean>
  >({});
  const [moduleFeatureLoadError, setModuleFeatureLoadError] = useState<
    string | null
  >(null);
  const [moduleFeatureSaving, setModuleFeatureSaving] = useState<string | null>(
    null,
  );
  const [moduleFeatureSaveError, setModuleFeatureSaveError] = useState<
    string | null
  >(null);
  const [initialWorkspaceName, setInitialWorkspaceName] = useState<string>(
    workspace?.name || "",
  );
  const [availableModuleIds, setAvailableModuleIds] = useState<string[]>([]);
  const [loadingModules, setLoadingModules] = useState<boolean>(true);
  const [showSaveSuccess, setShowSaveSuccess] = useState<boolean>(false);
  const [showHintsReset, setShowHintsReset] = useState<boolean>(false);
  const [skillOfferings, setSkillOfferings] =
    useState<WorkspaceSkillOfferingsResponse | null>(null);
  const [loadingSkillOfferings, setLoadingSkillOfferings] =
    useState<boolean>(false);
  const [skillOfferingsError, setSkillOfferingsError] = useState<string | null>(
    null,
  );
  const [togglingOfferingKey, setTogglingOfferingKey] = useState<string | null>(
    null,
  );
  const [
    skillInvocationAttributionEnabled,
    setSkillInvocationAttributionEnabled,
  ] = useState<boolean>(true);
  const [
    initialSkillInvocationAttributionEnabled,
    setInitialSkillInvocationAttributionEnabled,
  ] = useState<boolean>(true);
  const [
    skillInvocationAttributionDescription,
    setSkillInvocationAttributionDescription,
  ] = useState<string>(
    "Show inline chat badges when IDA invokes a skill during a response.",
  );
  const [followUpSuggestionsEnabled, setFollowUpSuggestionsEnabled] =
    useState<boolean>(false);
  const [
    initialFollowUpSuggestionsEnabled,
    setInitialFollowUpSuggestionsEnabled,
  ] = useState<boolean>(false);
  const [followUpSuggestionsDescription, setFollowUpSuggestionsDescription] =
    useState<string>(
      "Suggest two or three questions as clickable chips when a new chat opens and after each IDA answer.",
    );

  const title = "Workspace Settings";
  const hasIdaModule = enabledModules.includes("ida");

  useEffect(() => {
    if (!workspace?.id) {
      setModuleFeatureSchema({});
      setModuleFeatureValues({});
      setModuleFeatureLoadError(null);
      return;
    }

    let cancelled = false;
    setModuleFeatureGates({});
    setModuleFeatureLoadError(null);
    getWorkspaceModuleConfig(workspace.id)
      .then((response) => {
        if (cancelled) return;
        setModuleFeatureLoadError(null);
        const enabled = new Set(response.enabled);
        setModuleFeatureSchema(
          Object.fromEntries(
            Object.entries(response.feature_schema || {}).filter(([module]) =>
              enabled.has(module),
            ),
          ),
        );
        setModuleFeatureValues(response.config || {});
        setModuleFeatureGates(response.gates || {});
      })
      .catch(() => {
        if (!cancelled) {
          setModuleFeatureGates({});
          setModuleFeatureLoadError(
            "Couldn't load Module Features. Refresh the page to try again.",
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [getWorkspaceModuleConfig, workspace?.id]);

  const handleModuleFeatureChange = useCallback(
    async (featureKey: string, nextValue: boolean) => {
      if (!workspace?.id || moduleFeatureSaving) return;

      const previousValue = moduleFeatureValues[featureKey] === true;
      setModuleFeatureValues((current) => ({
        ...current,
        [featureKey]: nextValue,
      }));
      setModuleFeatureSaving(featureKey);
      setModuleFeatureSaveError(null);

      try {
        const updatedWorkspace = await workspaceUpdateAPI(workspace.id, {
          name: workspace.name,
          description: workspace.description || "",
          assistant_instructions: workspace.assistant_instructions || "",
          features: { [featureKey]: nextValue },
        });
        if (updatedWorkspace.features?.[featureKey] !== nextValue) {
          throw new Error("Workspace update did not persist module feature");
        }
        setModuleFeatureValues((current) => ({
          ...current,
          [featureKey]: nextValue,
        }));
        await refreshWorkspace();
      } catch {
        setModuleFeatureValues((current) => ({
          ...current,
          [featureKey]: previousValue,
        }));
        setModuleFeatureSaveError(
          "Could not update the module feature. Please try again.",
        );
      } finally {
        setModuleFeatureSaving(null);
      }
    },
    [
      moduleFeatureSaving,
      moduleFeatureValues,
      refreshWorkspace,
      workspace,
      workspaceUpdateAPI,
    ],
  );

  const { resetAllDismissed } = useTutorial();
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const handleResetOnboarding = useCallback(async () => {
    if (!workspace?.id) return;
    setResetting(true);
    try {
      await workspaceUpdateAPI(workspace.id, {
        name: workspace.name,
        description: "",
        assistant_instructions: "",
      });
      await resetAllDismissed();
      await refreshWorkspace();
    } finally {
      setResetting(false);
      setResetConfirmOpen(false);
    }
  }, [workspace, workspaceUpdateAPI, resetAllDismissed, refreshWorkspace]);

  // Lightweight, non-destructive reset: re-show all tutorial tours and hints
  // across every module (clears only the per-user dismissal store).
  const handleResetHints = useCallback(async () => {
    await resetAllDismissed();
    setShowHintsReset(true);
  }, [resetAllDismissed]);

  const moduleFeaturesTitleRef = useTutorialTarget("ida-module-features");
  useTutorialHint(
    {
      id: "ida-module-features-hint",
      targetId: "ida-module-features",
      title: "Module Features",
      body: "Enable or disable individual skills, toggle attribution badges in chat, and switch between Chat and Agent mode.",
      placement: "left",
    },
    isAdmin && hasIdaModule,
  );

  // Agent mode toggle (PR 1c, BE PR #2249). Admin-only UI; double-gated
  // server-side by env flag `ida_agent_mode` + workspace admin. 503 from
  // the BE means agent mode isn't provisioned for this env — we hide
  // the toggle entirely rather than expose configuration.
  const updateWorkspaceMode = useUpdateWorkspaceMode();
  const initialMode: "chat" | "agent" = workspace?.features?.mode ?? "chat";
  const [currentMode, setCurrentMode] = useState<"chat" | "agent">(initialMode);
  const [pendingMode, setPendingMode] = useState<"chat" | "agent" | null>(null);
  const [modeUpdating, setModeUpdating] = useState<boolean>(false);
  const [modeError, setModeError] = useState<string | null>(null);
  const [agentModeEnvFlagDisabled, setAgentModeEnvFlagDisabled] =
    useState<boolean>(false);
  const agentModeEnvFlagEnabled =
    workspace?.ui_env_flags?.ida_agent_mode ?? false;
  const agentModeControlVisible =
    hasIdaModule &&
    isAdmin &&
    agentModeEnvFlagEnabled &&
    !agentModeEnvFlagDisabled;

  // Keep the local mode in sync if the workspace context refreshes
  // (e.g. another admin flipped it in a different tab and we re-read).
  useEffect(() => {
    setCurrentMode(workspace?.features?.mode ?? "chat");
  }, [workspace?.features?.mode]);

  useEffect(() => {
    setAgentModeEnvFlagDisabled(false);
  }, [workspace?.id, agentModeEnvFlagEnabled]);

  const handleModeChangeRequest = useCallback(
    (newMode: "chat" | "agent") => {
      if (newMode === currentMode) return;
      setModeError(null);
      setPendingMode(newMode);
    },
    [currentMode],
  );

  const handleCancelModeChange = useCallback(() => {
    setPendingMode(null);
    setModeError(null);
  }, []);

  const handleConfirmModeChange = useCallback(async () => {
    if (!pendingMode || !workspace?.id) return;
    setModeUpdating(true);
    setModeError(null);
    try {
      const response = await updateWorkspaceMode(workspace.id, pendingMode);
      // Keep the confirmation open until shared workspace context reflects the
      // persisted mode, so callers returning to the workspace do not see stale
      // Chat-mode state.
      await refreshWorkspace();
      setCurrentMode(response.mode);
      setPendingMode(null);
    } catch (err) {
      const status = (err as Error & { status?: number }).status;
      const errBody = (
        err as Error & { response?: { code?: string; detail?: string } }
      ).response;
      if (status === 503) {
        // Env flag disabled — hide the toggle entirely. Non-admins
        // shouldn't see configuration unavailable in their env.
        setAgentModeEnvFlagDisabled(true);
        setPendingMode(null);
      } else if (status === 403) {
        // Defensive — UI already gates on admin.
        setModeError("You don't have permission to change the workspace mode.");
      } else if (status === 422 && errBody?.code === "agent_mode_cap_reached") {
        setModeError(
          errBody.detail ||
            (workspace.ida_agent_cap != null
              ? `Agent mode is capped at ${workspace.ida_agent_cap} active agents. Decommission unused agents to enable Agent mode.`
              : "The workspace agent cap has been reached. Decommission unused agents to enable Agent mode."),
        );
      } else {
        setModeError("Failed to update workspace mode. Please try again.");
      }
    } finally {
      setModeUpdating(false);
    }
  }, [
    pendingMode,
    workspace?.id,
    updateWorkspaceMode,
    refreshWorkspace,
    workspace?.ida_agent_cap,
  ]);

  const handlePublicSignupChange = useCallback(
    async (nextValue: boolean) => {
      if (!workspace?.id || publicSignupSaving) return;

      const previousValue = publicSignup;
      setPublicSignup(nextValue);
      setPublicSignupSaving(true);
      setPublicSignupError(null);

      try {
        const updatedWorkspace = await workspaceUpdateAPI(workspace.id, {
          name: workspace.name,
          description: workspace.description || "",
          assistant_instructions: workspace.assistant_instructions || "",
          features: { public_signup: nextValue },
        });
        if (updatedWorkspace.features?.public_signup !== nextValue) {
          throw new Error("Workspace update did not persist public signup");
        }
      } catch {
        setPublicSignup(previousValue);
        setPublicSignupError(
          "Could not update the workspace join setting. Please try again.",
        );
        setPublicSignupSaving(false);
        return;
      }

      try {
        await refreshWorkspace();
      } catch {
        setPublicSignupError(
          "The workspace join setting was saved, but the workspace could not be refreshed.",
        );
      } finally {
        setPublicSignupSaving(false);
      }
    },
    [
      workspace,
      publicSignup,
      publicSignupSaving,
      workspaceUpdateAPI,
      refreshWorkspace,
    ],
  );

  const { entries: integrationCatalogEntries } = useIntegrationCatalog(
    workspace?.organization_id ?? undefined,
  );
  const visibleSkillOfferings = React.useMemo(
    () =>
      filterWorkspaceSkillOfferingsByIntegrations(
        skillOfferings?.offerings || [],
        integrationCatalogEntries,
      ),
    [integrationCatalogEntries, skillOfferings?.offerings],
  );
  const visibleSkillOfferingGroups = React.useMemo(
    () => groupWorkspaceSkillOfferingsByCategory(visibleSkillOfferings),
    [visibleSkillOfferings],
  );

  // Always fetch fresh data when the settings page is opened.
  // Without this, the context may hold stale values (e.g. after the IDA agent
  // updated settings), and saving would silently overwrite them.
  useEffect(() => {
    refreshWorkspace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter module registry based on what's actually available on the backend
  const moduleOptions = React.useMemo(() => {
    return Object.entries(moduleRegistry)
      .filter(([moduleId]) => availableModuleIds.includes(moduleId))
      .map(([moduleId, moduleConfig]) => ({
        value: moduleId,
        label: moduleConfig.name,
      }));
  }, [availableModuleIds]);

  // What? A function to check if the workspace data has changed.
  // Why? To determine if the user has made any changes to the workspace data.
  // How?
  // - Compares the current values with the initial values.
  // - Sets isChanged to true if there are changes, false otherwise.
  useEffect(() => {
    const modulesChanged =
      JSON.stringify(enabledModules.sort()) !==
      JSON.stringify(initialEnabledModules.sort());
    const attributionChanged =
      skillInvocationAttributionEnabled !==
      initialSkillInvocationAttributionEnabled;
    const followUpSuggestionsChanged =
      followUpSuggestionsEnabled !== initialFollowUpSuggestionsEnabled;

    if (
      description !== initialDescription ||
      assistantInstructions !== initialAssistantInstructions ||
      workspaceName !== initialWorkspaceName ||
      modulesChanged ||
      attributionChanged ||
      followUpSuggestionsChanged
    ) {
      setIsChanged(true);
    } else {
      setIsChanged(false);
    }
  }, [
    description,
    assistantInstructions,
    workspaceName,
    enabledModules,
    initialDescription,
    initialAssistantInstructions,
    initialWorkspaceName,
    initialEnabledModules,
    skillInvocationAttributionEnabled,
    initialSkillInvocationAttributionEnabled,
    followUpSuggestionsEnabled,
    initialFollowUpSuggestionsEnabled,
  ]);

  // What? A function to handle the saving of workspace data.
  // Why? To update the workspace data in the API.
  // How?
  // - Checks if the workspace ID is valid.
  // - Tries to update the workspace data using workspaceUpdateAPI.
  // - Logs any errors that occur during the update process.
  const handleSave = async () => {
    if (workspace?.id) {
      setWorkspaceSaveError(null);
      try {
        await workspaceUpdateAPI(
          workspace.id,
          buildWorkspaceSettingsUpdatePayload({
            name: workspaceName,
            description,
            assistantInstructions,
            enabledModules,
            skillInvocationAttributionEnabled,
            followUpSuggestionsEnabled,
          }),
        );

        setInitialDescription(description);
        setInitialAssistantInstructions(assistantInstructions);
        setInitialWorkspaceName(workspaceName);
        setInitialEnabledModules(enabledModules);
        setInitialSkillInvocationAttributionEnabled(
          skillInvocationAttributionEnabled,
        );
        setInitialFollowUpSuggestionsEnabled(followUpSuggestionsEnabled);
        setIsChanged(false);
        setShowSaveSuccess(true);
        // Refresh the workspace context so navigation shows updated data
        await refreshWorkspace();
        await refreshFlags();
      } catch {
        setWorkspaceSaveError(
          "Could not save workspace settings. Please try again.",
        );
      }
    }
  };

  // What? A function to handle module selection changes.
  // Why? To update the enabled modules state when a radio button is selected.
  // How?
  // - Sets the enabled modules to a single-item array with the selected module.
  const handleModuleSelect = (moduleValue: string) => {
    setEnabledModules([moduleValue]);
  };

  // What? A function to enable the beforeunload warning only if there are unsaved changes.
  // Why? To prevent data loss if the user navigates away from the page without saving.
  // How?
  // - Adds an event listener for the beforeunload event.
  // - Sets the event return value to show the warning message.
  // - Removes the event listener when the component is unmounted.
  useEffect(() => {
    if (isChanged) {
      const handleBeforeUnload = (event: BeforeUnloadEvent) => {
        event.preventDefault();
        event.returnValue = ""; // Show the warning message
      };

      window.addEventListener("beforeunload", handleBeforeUnload);

      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }
  }, [isChanged]);

  useEffect(() => {
    setPublicSignup(persistedPublicSignup);
  }, [persistedPublicSignup]);

  useEffect(() => {
    if (workspace && !isChanged) {
      setDescription(workspace.description || "");
      setAssistantInstructions(workspace.assistant_instructions || "");
      setWorkspaceName(workspace.name || "");
      setInitialDescription(workspace.description || "");
      setInitialAssistantInstructions(workspace.assistant_instructions || "");
      setInitialWorkspaceName(workspace.name || "");
      setIsChanged(false);
    }
  }, [workspace, isChanged]);

  // Update enabled modules when context changes
  useEffect(() => {
    if (contextEnabledModules) {
      setEnabledModules(contextEnabledModules);
      setInitialEnabledModules(contextEnabledModules);
    }
  }, [contextEnabledModules]);

  // Fetch available modules from backend
  const fetchAvailableModules = useCallback(() => {
    setLoadingModules(true);
    getAvailableModules()
      .then((response: { enabled: string[]; config: any }) => {
        setAvailableModuleIds(response.enabled);
      })
      .catch((error: any) => {
        console.error("Failed to fetch available modules:", error);
        setAvailableModuleIds([]);
      })
      .finally(() => {
        setLoadingModules(false);
      });
  }, [getAvailableModules]);

  useEffect(() => {
    fetchAvailableModules();
  }, [fetchAvailableModules]);

  const fetchSkillOfferings = useCallback(async () => {
    if (!workspace?.id || !hasIdaModule) {
      setSkillOfferings(null);
      setSkillOfferingsError(null);
      setLoadingSkillOfferings(false);
      return null;
    }

    setLoadingSkillOfferings(true);
    setSkillOfferingsError(null);
    try {
      const response = await getSkillOfferings(workspace.id);
      setSkillOfferings(response);
      return response;
    } catch (error: any) {
      console.error("Failed to fetch workspace skill offerings:", error);
      setSkillOfferings(null);
      setSkillOfferingsError(
        "Failed to load workspace skills. Please try again.",
      );
      return null;
    } finally {
      setLoadingSkillOfferings(false);
    }
  }, [workspace?.id, hasIdaModule, getSkillOfferings]);

  useEffect(() => {
    fetchSkillOfferings();
  }, [fetchSkillOfferings]);

  useEffect(() => {
    const fetchAttributionFlag = async () => {
      if (!workspace?.id || !hasIdaModule) {
        return;
      }
      try {
        const response = await getFeatureFlags(workspace.id);
        const enabled = response.flags.skill_invocation_attribution !== false;
        setSkillInvocationAttributionEnabled(enabled);
        setInitialSkillInvocationAttributionEnabled(enabled);
        const description =
          response.feature_schema?.ida?.skill_invocation_attribution
            ?.description;
        if (description) {
          setSkillInvocationAttributionDescription(description);
        }
        const followUpEnabled =
          response.flags.ida_follow_up_suggestions === true;
        setFollowUpSuggestionsEnabled(followUpEnabled);
        setInitialFollowUpSuggestionsEnabled(followUpEnabled);
        const followUpDescription =
          response.feature_schema?.ida?.ida_follow_up_suggestions?.description;
        if (followUpDescription) {
          setFollowUpSuggestionsDescription(followUpDescription);
        }
      } catch (error) {
        console.error("Failed to fetch workspace feature flags:", error);
      }
    };

    fetchAttributionFlag();
  }, [getFeatureFlags, hasIdaModule, workspace?.id]);

  const replaceOffering = useCallback(
    (updatedOffering: WorkspaceSkillOfferingRead) => {
      setSkillOfferings((current) => {
        if (!current) return current;
        return {
          ...current,
          offerings: current.offerings.map((offering) =>
            offering.offering_key === updatedOffering.offering_key
              ? updatedOffering
              : offering,
          ),
        };
      });
    },
    [],
  );

  const handleSkillOfferingToggle = async (
    offering: WorkspaceSkillOfferingRead,
    enabled: boolean,
  ) => {
    if (!workspace?.id || !isAdmin || !offering.toggleable) return;

    setTogglingOfferingKey(offering.offering_key);
    setSkillOfferingsError(null);
    try {
      const updated = await toggleSkillOffering(
        workspace.id,
        offering.offering_key,
        enabled,
        offering.version ?? 0,
      );
      replaceOffering(updated);
    } catch (error: any) {
      const detail = error?.response?.detail;
      if (detail && typeof detail === "object") {
        if (detail.code === "integration_not_enabled_in_workspace") {
          setSkillOfferingsError(
            "Enable Deltek in this workspace (Settings > Integrations) first.",
          );
          return;
        }
        if (detail.code === "user_account_not_connected") {
          setSkillOfferingsError("Connect your Deltek account first.");
          return;
        }
      }
      if (error?.status === 409) {
        const refreshed = await fetchSkillOfferings();
        const latest = refreshed?.offerings.find(
          (item) => item.offering_key === offering.offering_key,
        );
        if (latest?.enabled === enabled) {
          replaceOffering(latest);
          return;
        }
        if (latest) {
          try {
            const updated = await toggleSkillOffering(
              workspace.id,
              latest.offering_key,
              enabled,
              latest.version ?? 0,
            );
            replaceOffering(updated);
            return;
          } catch (retryError: any) {
            console.error(
              "Failed to retry workspace skill toggle:",
              retryError,
            );
          }
        }
        setSkillOfferingsError(
          "Skill state changed while you were editing. The list has been refreshed; please try again.",
        );
      } else {
        console.error("Failed to update workspace skill offering:", error);
        setSkillOfferingsError(
          "Failed to update workspace skill. Please try again.",
        );
      }
    } finally {
      setTogglingOfferingKey(null);
    }
  };

  const renderIncludedSkills = (skills: string[]) => (
    <>
      <Typography level="body-xs" color="neutral" sx={{ mb: 1 }}>
        Included skills:
      </Typography>
      <List size="sm" sx={{ py: 0 }}>
        {skills.map((skill) => (
          <ListItem key={skill} sx={{ py: 0.25 }}>
            <ListItemDecorator>
              <CheckRoundedIcon sx={{ fontSize: 14 }} />
            </ListItemDecorator>
            <Typography level="body-sm">{skill}</Typography>
          </ListItem>
        ))}
      </List>
    </>
  );

  const renderBaselineSkills = (skills: WorkspaceBaselineSkillRead[]) => (
    <List size="sm" sx={{ py: 0 }}>
      {skills.map((skill) => (
        <ListItem key={skill.skill_key} sx={{ py: 0.25 }}>
          <ListItemDecorator>
            {skill.environment_available !== false ? (
              <CheckRoundedIcon sx={{ fontSize: 14 }} />
            ) : (
              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
            )}
          </ListItemDecorator>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography level="body-sm">
              {skill.display_name || skill.skill_key}
            </Typography>
            {skill.status === "deprecated" && (
              <Chip size="sm" variant="soft" color="warning">
                Deprecated
              </Chip>
            )}
            {skill.environment_available === false && (
              <Chip size="sm" variant="soft" color="neutral">
                Not available in this environment
              </Chip>
            )}
          </Stack>
        </ListItem>
      ))}
    </List>
  );

  const renderSkillOfferingAccordion = (
    offering: WorkspaceSkillOfferingRead,
  ) => {
    const isToggling = togglingOfferingKey === offering.offering_key;
    const isDeprecated = offering.status === "deprecated";
    const configBlocksEnable =
      offering.requires_configuration &&
      !offering.configuration_complete &&
      !offering.enabled;
    const toggleDisabled =
      isToggling ||
      !isAdmin ||
      !offering.toggleable ||
      offering.skill_keys.length === 0 ||
      configBlocksEnable;

    return (
      <Accordion
        key={offering.offering_key}
        disabled={offering.skill_keys.length === 0}
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          "&:last-child": { borderBottom: "none" },
        }}
      >
        <AccordionSummary
          indicator={offering.skill_keys.length > 0 ? undefined : null}
          sx={{
            py: 1.5,
            "& .MuiAccordionSummary-content": {
              flexGrow: 1,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              pr: 1,
            }}
          >
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography level="title-sm">
                  {offering.display_name}
                </Typography>
                {isDeprecated && (
                  <Chip size="sm" variant="soft" color="warning">
                    Deprecated
                  </Chip>
                )}
              </Stack>
              <Typography level="body-sm" color="neutral">
                {isDeprecated
                  ? offering.enabled
                    ? "Existing assignment remains enabled; new enablement is unavailable."
                    : "This skill is no longer available for new enablement."
                  : configBlocksEnable
                    ? "Configuration is required before this skill can be enabled."
                    : offering.enabled
                      ? "Enabled in this workspace."
                      : "Available to this workspace."}
              </Typography>
            </Box>
            <Switch
              slotProps={{
                input: {
                  "aria-label": `${offering.display_name} enabled`,
                },
              }}
              checked={offering.enabled}
              disabled={toggleDisabled}
              onChange={(e) => {
                e.stopPropagation();
                handleSkillOfferingToggle(offering, e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </Box>
        </AccordionSummary>
        {offering.skill_keys.length > 0 && (
          <AccordionDetails>
            {configBlocksEnable && (
              <Alert color="warning" variant="soft" size="sm" sx={{ mb: 1.5 }}>
                Configuration is required before this skill can be enabled.
              </Alert>
            )}
            {!isAdmin && (
              <FormHelperText sx={{ mb: 1 }}>
                Only workspace admins can change this skill.
              </FormHelperText>
            )}
            {renderIncludedSkills(offering.skill_keys)}
          </AccordionDetails>
        )}
      </Accordion>
    );
  };

  return (
    <Box sx={{ p: 3, maxWidth: "1200px", mx: "auto" }}>
      {/* Header Section */}
      <Stack spacing={3}>
        <Box>
          <Typography level="h2" sx={{ mb: 1 }}>
            {title}
          </Typography>
          <Typography level="body-md" color="neutral">
            Manage your workspace configuration and settings
          </Typography>
        </Box>

        {/* Workspace Mode (PR 1c / Agent mode foundation).
            Admin-only; only rendered when IDA module is enabled and the
            backend's curated ui_env_flags payload says ida_agent_mode is
            enabled for this environment. The 503 handler remains as a
            mid-session defensive fallback. */}
        {agentModeControlVisible && (
          <Card variant="outlined" sx={{ p: 3 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
            >
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography level="h3">Agent Mode</Typography>
                  <Tooltip
                    title="Agent Mode changes how this whole workspace creates new conversations. New conversations become persistent agents with personas and inter-agent communication. Existing chats remain unchanged."
                    variant="soft"
                    placement="top"
                  >
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="neutral"
                      aria-label="Agent mode information"
                      sx={{ p: 0.5 }}
                    >
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Typography level="body-sm" color="neutral" sx={{ mt: 0.5 }}>
                  {currentMode === "agent"
                    ? "New conversations use persistent Agent mode."
                    : "New conversations use standard Chat mode."}
                </Typography>
              </Box>
              <Switch
                checked={currentMode === "agent"}
                disabled={modeUpdating}
                onChange={(event) =>
                  handleModeChangeRequest(
                    event.target.checked ? "agent" : "chat",
                  )
                }
                slotProps={{ input: { "aria-label": "Agent Mode" } }}
              />
            </Stack>
            {modeError && (
              <Alert color="danger" size="sm" sx={{ mt: 2 }}>
                {modeError}
              </Alert>
            )}
          </Card>
        )}

        {/* Admin "Recently decommissioned agents" panel. Surfaces the user-
            driven recovery path for decommission_agent / resurrect_agent
            skills. Only rendered to admins on workspaces currently in Agent
            mode — outside that window the panel has no actionable content. */}
        {agentModeControlVisible &&
          currentMode === "agent" &&
          workspace?.id && (
            <RecentlyDecommissionedPanel workspaceId={workspace.id} />
          )}

        {/* Basic Workspace Settings */}
        <Card variant="outlined" sx={{ p: 3 }}>
          <Typography level="h3" sx={{ mb: 0.5 }}>
            Basic Settings
          </Typography>
          <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
            The workspace name and description are visible to all members.
            Assistant instructions shape how the AI responds — similar to a
            system prompt for this workspace.
          </Typography>

          <Stack spacing={3}>
            <FormControl>
              <FormLabel>Workspace Name</FormLabel>
              <Input
                placeholder="Set a name for this workspace"
                value={workspaceName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setWorkspaceName(e.target.value)
                }
                readOnly={!isAdmin}
                variant={isAdmin ? "outlined" : "soft"}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                minRows={4}
                placeholder="Add a description for this workspace"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                readOnly={!isAdmin}
                variant={isAdmin ? "outlined" : "soft"}
              />
            </FormControl>
          </Stack>
        </Card>

        {/* Administrator Settings */}
        {isAdmin && (
          <Card variant="outlined" sx={{ p: 3 }}>
            <Typography level="h3" sx={{ mb: 0.5 }}>
              Administrator Settings
            </Typography>
            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
              Enable or disable modules for this workspace. Disabled modules are
              hidden from all users.
            </Typography>

            <Stack spacing={3}>
              <FormControl>
                <FormLabel>Assistant Instructions</FormLabel>
                <Textarea
                  minRows={4}
                  placeholder="Add AI assistant instructions for this workspace"
                  value={assistantInstructions}
                  onChange={(e) => setAssistantInstructions(e.target.value)}
                />
              </FormControl>

              <PublicSignupToggle
                checked={publicSignup}
                disabled={publicSignupSaving}
                onChange={handlePublicSignupChange}
              />
              {publicSignupError && (
                <Alert color="danger" role="alert" size="sm">
                  {publicSignupError}
                </Alert>
              )}

              <Box>
                <Typography level="title-sm" sx={{ mb: 1 }}>
                  Module
                </Typography>
                <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                  Select the module for this workspace:
                </Typography>

                {loadingModules ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 2 }}
                  >
                    <CircularProgress />
                  </Box>
                ) : moduleOptions.length === 0 ? (
                  <Alert color="warning">
                    No modules are currently available. Please contact your
                    administrator.
                  </Alert>
                ) : (
                  <RadioGroup
                    value={enabledModules[0] || ""}
                    onChange={(e) => handleModuleSelect(e.target.value)}
                  >
                    {moduleOptions.map((module) => (
                      <Radio
                        key={module.value}
                        value={module.value}
                        label={module.label}
                        size="sm"
                      />
                    ))}
                  </RadioGroup>
                )}

                {enabledModules.length === 0 &&
                  !loadingModules &&
                  moduleOptions.length > 0 && (
                    <Alert color="danger" sx={{ mt: 2 }}>
                      Please select a module
                    </Alert>
                  )}
              </Box>
            </Stack>
          </Card>
        )}

        {/* Module Features Section */}
        {hasIdaModule && (
          <Card variant="outlined" sx={{ p: 3 }}>
            <Typography
              ref={moduleFeaturesTitleRef}
              level="h3"
              sx={{ mb: 0.5 }}
            >
              Module Features
            </Typography>
            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
              Fine-tune IDA's behaviour — toggle individual skills, control how
              attribution badges appear in chat, suggest follow-up questions
              after answers, and switch between Chat mode (Q&amp;A) and Agent
              mode (autonomous task execution).
            </Typography>
            {moduleFeatureLoadError && (
              <Alert color="danger" sx={{ mb: 2 }}>
                {moduleFeatureLoadError}
              </Alert>
            )}
            {moduleFeatureSaveError && (
              <Alert color="danger" sx={{ mb: 2 }}>
                {moduleFeatureSaveError}
              </Alert>
            )}
            <Box
              sx={{
                p: 2,
                mb: 3,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "background.level1",
              }}
            >
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography level="title-sm">
                    Follow-up Suggestions
                  </Typography>
                  <Typography level="body-sm" color="neutral">
                    {followUpSuggestionsDescription}
                  </Typography>
                </Box>
                <Switch
                  checked={followUpSuggestionsEnabled}
                  disabled={!isAdmin}
                  onChange={(event) =>
                    setFollowUpSuggestionsEnabled(event.target.checked)
                  }
                  slotProps={{
                    input: { "aria-label": "Follow-up Suggestions" },
                  }}
                />
              </Stack>
            </Box>
            {loadingSkillOfferings ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress />
              </Box>
            ) : skillOfferingsError ? (
              <Alert
                color="danger"
                endDecorator={
                  <Button
                    size="sm"
                    variant="soft"
                    color="danger"
                    onClick={() => fetchSkillOfferings()}
                  >
                    Retry
                  </Button>
                }
              >
                {skillOfferingsError}
              </Alert>
            ) : !skillOfferings ||
              (skillOfferings.baseline_skills.length === 0 &&
                visibleSkillOfferings.length === 0) ? (
              <Alert color="neutral" variant="soft">
                No workspace skills are available yet.
              </Alert>
            ) : (
              <Stack spacing={3}>
                <Box
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "background.level1",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography level="title-sm">
                        Skill Invocation Attribution
                      </Typography>
                      <Typography level="body-sm" color="neutral">
                        {skillInvocationAttributionDescription}
                      </Typography>
                    </Box>
                    <Switch
                      checked={skillInvocationAttributionEnabled}
                      disabled={!isAdmin}
                      onChange={(event) =>
                        setSkillInvocationAttributionEnabled(
                          event.target.checked,
                        )
                      }
                    />
                  </Stack>
                </Box>
                <Stack spacing={2}>
                  {skillOfferings.baseline_skills.length > 0 && (
                    <AccordionGroup variant="plain" sx={{ gap: 0 }}>
                      <Accordion
                        sx={{
                          borderBottom: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <AccordionSummary
                          sx={{
                            py: 1.5,
                            "& .MuiAccordionSummary-content": {
                              flexGrow: 1,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              width: "100%",
                              pr: 1,
                            }}
                          >
                            <Box>
                              <Typography level="title-sm">
                                Baseline Skills
                              </Typography>
                              <Typography level="body-sm" color="neutral">
                                Baseline skills cannot be changed here;
                                availability can depend on the environment.
                              </Typography>
                            </Box>
                            <Switch checked disabled />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          {renderBaselineSkills(skillOfferings.baseline_skills)}
                        </AccordionDetails>
                      </Accordion>
                    </AccordionGroup>
                  )}
                  {visibleSkillOfferingGroups.map((group) => (
                    <Box key={group.key}>
                      <Typography level="title-sm" sx={{ mb: 1 }}>
                        {group.label}
                      </Typography>
                      <AccordionGroup variant="plain" sx={{ gap: 0 }}>
                        {group.offerings.map(renderSkillOfferingAccordion)}
                      </AccordionGroup>
                    </Box>
                  ))}
                </Stack>
              </Stack>
            )}
          </Card>
        )}

        {isAdmin &&
          hasIdaModule &&
          workspace &&
          !featureFlagsLoading &&
          environmentFeatureFlags.ida_custom_skills === true && (
            <WorkspaceCustomSkills
              workspaceId={workspace.id}
              isAdmin={isAdmin}
            />
          )}

        {/* Integrations Section */}
        {isAdmin && (
          <Card variant="outlined" sx={{ p: 3 }} id="integrations">
            <Typography level="h3" sx={{ mb: 0.5 }}>
              Integrations
            </Typography>
            <Typography level="body-sm" color="neutral" sx={{ mb: 3 }}>
              Connect external services so IDA can read from or write to them
              during conversations.
            </Typography>
            {workspace && (
              <WorkspaceIntegrationSelections
                workspaceId={workspace.id}
                organizationId={workspace.organization_id ?? undefined}
                enabledModules={enabledModules}
                emailEnabled={orgEmailStatus.enabled}
                onSelectionsChanged={refreshWorkspace}
              />
            )}
          </Card>
        )}

        {/* Help & onboarding */}
        <Card variant="outlined" sx={{ p: 3 }}>
          <Typography level="h3" sx={{ mb: 0.5 }}>
            Help &amp; onboarding
          </Typography>
          <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
            Show the guided tours and tip bubbles again across all modules.
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography level="title-sm">Reset tutorial hints</Typography>
              <Typography level="body-sm" color="neutral">
                Re-enables every tutorial tour and hint you've dismissed.
              </Typography>
            </Box>
            <Button variant="soft" color="neutral" onClick={handleResetHints}>
              Reset tutorial hints
            </Button>
          </Stack>
        </Card>

        {/* Developer Tools */}
        {isAdmin && isDevFeaturesEnabled() && hasIdaModule && (
          <Card
            variant="outlined"
            sx={{ p: 3, borderColor: "warning.outlinedBorder" }}
          >
            <Typography level="h3" sx={{ mb: 0.5 }} color="warning">
              Developer Tools
            </Typography>
            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
              These actions are only visible in development mode.
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography level="title-sm">Reset onboarding</Typography>
                <Typography level="body-sm" color="neutral">
                  Clears workspace description and assistant instructions,
                  forcing IDA's setup conversation to re-run on next visit.
                </Typography>
              </Box>
              <Button
                color="danger"
                variant="soft"
                onClick={() => setResetConfirmOpen(true)}
              >
                Reset
              </Button>
            </Stack>
          </Card>
        )}

        {/* Save Button */}
        {isAdmin && (
          <Stack spacing={1}>
            {workspaceSaveError && (
              <Alert color="danger" role="alert" size="sm">
                {workspaceSaveError}
              </Alert>
            )}
            <Stack direction="row" justifyContent="flex-end">
              <Button
                disabled={
                  !isChanged ||
                  publicSignupSaving ||
                  enabledModules.length === 0 ||
                  loadingModules ||
                  moduleOptions.length === 0
                }
                onClick={handleSave}
                variant={
                  isChanged && enabledModules.length > 0 ? "solid" : "outlined"
                }
                color={
                  isChanged && enabledModules.length > 0 ? "primary" : "neutral"
                }
              >
                Save Changes
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>

      {/* Workspace mode confirmation modal — required UX per design §3.8.
          Modal text differs by direction (chat→agent vs agent→chat) so
          users understand what their existing conversations will do post-
          flip. Confirm calls handleConfirmModeChange which routes through
          updateWorkspaceMode; Cancel just clears pendingMode. */}
      <Modal
        open={pendingMode !== null}
        onClose={() => {
          if (!modeUpdating) handleCancelModeChange();
        }}
      >
        <ModalDialog
          variant="outlined"
          role="alertdialog"
          sx={{ maxWidth: 480 }}
        >
          <DialogTitle>
            {pendingMode === "agent"
              ? "Switch to Agent mode?"
              : "Switch to Chat mode?"}
          </DialogTitle>
          <Divider />
          <DialogContent>
            {pendingMode === "agent" ? (
              <Stack spacing={1.5}>
                <Typography level="body-md">
                  Agent mode enables persistent agents and agent-to-agent
                  collaboration in this workspace.
                </Typography>
                <Box>
                  <Typography level="body-sm" fontWeight="md" sx={{ mb: 0.5 }}>
                    What this changes:
                  </Typography>
                  <List
                    size="sm"
                    marker="disc"
                    sx={{ "--ListItem-paddingY": "0.125rem" }}
                  >
                    <ListItem>
                      New conversations are created as agents with names and
                      personas
                    </ListItem>
                    <ListItem>
                      Agents appear in the workspace agent list, visible to all
                      members
                    </ListItem>
                    <ListItem>
                      Agents can send messages to each other and run skills
                      autonomously
                    </ListItem>
                    <ListItem>
                      Existing chat conversations remain unchanged and stay
                      accessible
                    </ListItem>
                  </List>
                </Box>
                <Typography level="body-sm" color="neutral">
                  You can switch back to Chat mode at any time. Agents created
                  here remain in the workspace after the switch.
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={1.5}>
                <Typography level="body-md">
                  Chat mode disables persistent agents and agent-to-agent
                  collaboration.
                </Typography>
                <Box>
                  <Typography level="body-sm" fontWeight="md" sx={{ mb: 0.5 }}>
                    What this changes:
                  </Typography>
                  <List
                    size="sm"
                    marker="disc"
                    sx={{ "--ListItem-paddingY": "0.125rem" }}
                  >
                    <ListItem>
                      New conversations are created as standard chats
                    </ListItem>
                    <ListItem>
                      Existing agents become regular chats — you can still
                      message them directly, but they won't respond to other
                      agents or trigger autonomous actions
                    </ListItem>
                    <ListItem>
                      Agent UI elements (status, heartbeat, agent list) hide
                      from the workspace
                    </ListItem>
                    <ListItem>Conversation history is preserved</ListItem>
                  </List>
                </Box>
                <Typography level="body-sm" color="neutral">
                  You can switch back to Agent mode at any time. Existing agents
                  resume their behaviors when you do.
                </Typography>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="solid"
              color="primary"
              onClick={handleConfirmModeChange}
              disabled={modeUpdating}
              startDecorator={
                modeUpdating ? <CircularProgress size="sm" /> : undefined
              }
            >
              Confirm
            </Button>
            <Button
              variant="plain"
              color="neutral"
              onClick={handleCancelModeChange}
              disabled={modeUpdating}
            >
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Reset onboarding confirmation modal */}
      <Modal
        open={resetConfirmOpen}
        onClose={() => !resetting && setResetConfirmOpen(false)}
      >
        <ModalDialog
          variant="outlined"
          role="alertdialog"
          sx={{ maxWidth: 420 }}
        >
          <DialogTitle>Reset onboarding?</DialogTitle>
          <Divider />
          <DialogContent>
            <Typography level="body-md">
              This will clear the workspace <strong>description</strong> and{" "}
              <strong>assistant instructions</strong>, returning it to an
              uninitialized state. IDA's setup conversation will be required
              again on next visit.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              color="danger"
              loading={resetting}
              onClick={handleResetOnboarding}
            >
              Reset
            </Button>
            <Button
              variant="plain"
              color="neutral"
              disabled={resetting}
              onClick={() => setResetConfirmOpen(false)}
            >
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Success Snackbar */}
      <Snackbar
        open={showSaveSuccess}
        autoHideDuration={2000}
        onClose={() => setShowSaveSuccess(false)}
        color="success"
        variant="soft"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        Settings saved successfully
      </Snackbar>

      {/* Tutorial hints reset Snackbar */}
      <Snackbar
        open={showHintsReset}
        autoHideDuration={3000}
        onClose={() => setShowHintsReset(false)}
        color="success"
        variant="soft"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        Tutorial hints reset — tours and tips will appear again.
      </Snackbar>
    </Box>
  );
};

export default WorkspaceSettings;
